import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import { SportsScore as MatchIcon } from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { getBracketNames } from "../utils/bracketNames";
import { getRoundLabel } from "../utils/roundLabels";
import { getOrgGradient } from "../utils/orgGradient";
import { useOrganization } from "../contexts/OrganizationContext";
import { TournamentOpponentStat } from "../hooks/useTournamentStats";

interface HeadToHeadMatch {
  tournamentId: string;
  tournamentName: string;
  startDate?: string | Date;
  stage: "group" | "knockout" | "americana";
  team1Score: number;
  team2Score: number;
  groupName?: string;
  round?: string;
  roundLabel?: string;
  roundNumber?: number;
  knockoutType?: "masters-cup" | "tour-cup";
  startTime?: string;
  player1PartnerId?: string | null;
  player1PartnerName?: string;
  player2PartnerId?: string | null;
  player2PartnerName?: string;
}

interface HeadToHeadStats {
  player1: { id: string; name: string };
  player2: { id: string; name: string };
  player1Wins: number;
  player2Wins: number;
  draws: number;
  totalMatches: number;
  matches: HeadToHeadMatch[];
}

interface TournamentHeadToHeadCardProps {
  playerId: string;
  opponents: TournamentOpponentStat[];
}

const TournamentHeadToHeadCard: React.FC<TournamentHeadToHeadCardProps> = ({
  playerId,
  opponents,
}) => {
  const theme = useTheme();
  const { currentOrg } = useOrganization();
  const bracketNames = getBracketNames(currentOrg?.slug);
  const gradient = getOrgGradient(currentOrg?.slug);
  const [selected, setSelected] = useState<TournamentOpponentStat | null>(null);
  const [stats, setStats] = useState<HeadToHeadStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelect = async (opponent: TournamentOpponentStat | null) => {
    setSelected(opponent);
    setStats(null);
    setError(null);
    if (!opponent) return;

    try {
      setLoading(true);
      const response = await apiRequest(
        `/api/players/${playerId}/tournament-head-to-head/${opponent.opponentId}`
      );
      if (!response.ok) {
        throw new Error("Failed to load head-to-head stats");
      }
      const json = await response.json();
      setStats(json);
    } catch (err) {
      console.error("Error fetching tournament head-to-head:", err);
      setError("Could not load head-to-head record");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          <MatchIcon color="primary" /> Head-to-Head
        </Typography>

        {opponents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No tournament opponents recorded yet.
          </Typography>
        ) : (
          <>
            <Autocomplete
              options={opponents}
              getOptionLabel={(opt) =>
                `${opt.opponentName} (${opt.matchesPlayed} match${opt.matchesPlayed !== 1 ? "es" : ""})`
              }
              value={selected}
              onChange={(_, value) => handleSelect(value)}
              isOptionEqualToValue={(opt, val) => opt.opponentId === val.opponentId}
              renderInput={(params) => (
                <TextField {...params} label="Select an opponent" size="small" />
              )}
              sx={{ mb: 3 }}
            />

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && stats && (
              <Box>
                {/* VS scoreboard */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    mb: 3,
                  }}
                >
                  <Box sx={{ textAlign: "center", flex: 1, minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        mx: "auto",
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: theme.palette.primary.main,
                        fontWeight: "bold",
                        fontSize: "1.4rem",
                        border: `2px solid ${theme.palette.primary.main}`,
                      }}
                    >
                      {stats.player1.name?.charAt(0) || "?"}
                    </Avatar>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        mt: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {stats.player1.name}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "center", flexShrink: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 2,
                        py: 1,
                        borderRadius: 99,
                        background: gradient,
                        color: "white",
                        boxShadow: 2,
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: "bold", lineHeight: 1 }}>
                        {stats.player1Wins}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        -
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.85, lineHeight: 1 }}>
                        {stats.draws}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        -
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold", lineHeight: 1 }}>
                        {stats.player2Wins}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {stats.totalMatches} match{stats.totalMatches !== 1 ? "es" : ""}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "center", flex: 1, minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        mx: "auto",
                        bgcolor: alpha(theme.palette.secondary.main, 0.15),
                        color: theme.palette.secondary.main,
                        fontWeight: "bold",
                        fontSize: "1.4rem",
                        border: `2px solid ${theme.palette.secondary.main}`,
                      }}
                    >
                      {stats.player2.name?.charAt(0) || "?"}
                    </Avatar>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        mt: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {stats.player2.name}
                    </Typography>
                  </Box>
                </Box>

                {stats.matches.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No recorded matches between these players.
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {stats.matches.map((match, index) => {
                      const isDraw = match.team1Score === match.team2Score;
                      const player1Won = match.team1Score > match.team2Score;
                      const accentColor = isDraw
                        ? theme.palette.grey[500]
                        : player1Won
                        ? theme.palette.primary.main
                        : theme.palette.secondary.main;
                      const sectionLabel =
                        match.stage === "group"
                          ? match.groupName
                            ? `Group Stage — ${match.groupName}`
                            : "Group Stage"
                          : match.stage === "americana"
                          ? `Round ${match.roundNumber}`
                          : `${
                              match.knockoutType === "masters-cup"
                                ? bracketNames.primary
                                : bracketNames.secondary
                            } — ${match.roundLabel || getRoundLabel(match.round || "")}`;

                      return (
                        <Paper
                          key={`${match.tournamentId}-${index}`}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            borderLeft: `4px solid ${accentColor}`,
                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": { transform: "translateY(-2px)", boxShadow: 2 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                              {match.tournamentName}
                            </Typography>
                            <Chip
                              label={isDraw ? "Draw" : player1Won ? `${stats.player1.name} won` : `${stats.player2.name} won`}
                              size="small"
                              sx={{
                                fontWeight: "bold",
                                bgcolor: alpha(accentColor, 0.15),
                                color: accentColor,
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                            {sectionLabel}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Box sx={{ textAlign: "left", minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: player1Won ? "bold" : 500 }}
                                noWrap
                              >
                                {stats.player1.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                & {match.player1PartnerName || "Unknown"}
                              </Typography>
                            </Box>

                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold", color: accentColor, flexShrink: 0, px: 1 }}
                            >
                              {match.team1Score}-{match.team2Score}
                            </Typography>

                            <Box sx={{ textAlign: "right", minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: !isDraw && !player1Won ? "bold" : 500 }}
                                noWrap
                              >
                                {stats.player2.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                & {match.player2PartnerName || "Unknown"}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}

            {!loading && !stats && !error && (
              <Typography variant="body2" color="text.secondary">
                Pick an opponent above to see your tournament record against them.
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TournamentHeadToHeadCard;
